import React from 'react';
import CorrectIcon from './CorrectIcon';
import WrongIcon from './WrongIcon';
import { useQuestionCardStyle } from '../contexts/QuestionCardStyleContext';

interface QuestionCardProps {
  points: number | string;
  earnedPoints?: number;
  children: React.ReactNode;
  className?: string;
  hidePointsIcon?: boolean;
  questionNumber?: number;
  isDailyDouble?: boolean;
  isCorrect?: boolean | null;
  'data-testid'?: string;
  'data-test-type'?: string;
}

/**
 * QuestionCard - Common wrapper for all question types
 * Provides consistent layout with points badge and card styling
 * Figma: CD — Pick-em over-under shell (10203:60669 / 10203:60905)
 */
const QuestionCard: React.FC<QuestionCardProps> = ({
  points,
  earnedPoints,
  children,
  className = '',
  hidePointsIcon = false,
  questionNumber,
  isCorrect,
  'data-testid': dataTestId,
  'data-test-type': dataTestType,
}) => {
  const {
    bgGradientStart,
    bgGradientEnd,
    borderColor,
    questionNumberBg,
    questionNumberFg,
  } = useQuestionCardStyle();
  const gradientStyle: React.CSSProperties =
    bgGradientStart || bgGradientEnd
      ? {
          background: `linear-gradient(180deg, ${bgGradientStart ?? bgGradientEnd}, ${bgGradientEnd ?? bgGradientStart})`,
        }
      : {};

  const dynamicBorderStyle: React.CSSProperties = borderColor ? { borderColor } : {};

  const pointsDisplay = () => {
    if (earnedPoints !== undefined) {
      // Results mode - show earned/potential format
      const potentialPoints = typeof points === 'number' ? points : parseInt(String(points));
      return `${earnedPoints}/${potentialPoints}pts`;
    }
    // Selection mode - show just potential points
    const pts = typeof points === 'number' ? points : parseInt(String(points)) || 0;
    return `-- / ${pts}pts`;
  };

  // Daily Double chrome is unused for Circus Daily — keep default card surface.
  const cardBackground = 'bg-surface';
  const cardBorder = 'border-border';

  // Number badge: result state overrides style_config colors.
  const hasResult = typeof isCorrect === 'boolean';
  const numberBackground = hasResult
    ? isCorrect
      ? 'bg-[#3bbe1e]'
      : 'bg-[#e02125]'
    : 'bg-white';
  const numberTextColor = hasResult ? 'text-background' : 'text-primary';

  return (
    <div
      className={`relative flex flex-col gap-4 ${cardBackground} border ${cardBorder} rounded-2xl pt-6 pb-4 px-4 ${className}`}
      style={{ ...gradientStyle, ...dynamicBorderStyle }}
      data-testid={dataTestId}
      data-test-type={dataTestType}
    >
      {/* Question Number Circle - Top Center */}
      {!hidePointsIcon && questionNumber && (
        <div className="absolute left-1/2 top-0 -translate-x-1/2 -translate-y-1/2">
          <div className="relative">
            <div
              className={`w-7 h-7 ${numberBackground} rounded-full flex items-center justify-center border-[3px] border-background relative z-10`}
              style={
                hasResult
                  ? undefined
                  : questionNumberBg
                    ? { backgroundColor: questionNumberBg }
                    : undefined
              }
              data-test-result={hasResult ? (isCorrect ? 'correct' : 'incorrect') : undefined}
            >
              <span
                className={`${numberTextColor} font-sans font-semibold text-xs`}
                style={
                  hasResult
                    ? undefined
                    : questionNumberFg
                      ? { color: questionNumberFg }
                      : undefined
                }
              >
                {questionNumber}
              </span>
            </div>
            {/* Bottom semi-circle border */}
            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 w-[30px] h-[15px] overflow-hidden"
              style={{ zIndex: 5 }}
            >
              <div
                className={`w-[30px] h-[30px] rounded-full border ${cardBorder} -translate-y-1/2`}
                style={dynamicBorderStyle}
              />
            </div>
          </div>
        </div>
      )}

      {/* Question Content */}
      {children}

      {/* Points Display - Bottom Center */}
      {!hidePointsIcon && (
        <div className="flex justify-center items-center gap-2">
          {typeof isCorrect === 'boolean' && (isCorrect ? <CorrectIcon /> : <WrongIcon />)}
          <span className="text-muted-foreground font-sans text-label-bold capitalize text-center whitespace-nowrap">
            {pointsDisplay()}
          </span>
        </div>
      )}
    </div>
  );
};

export default QuestionCard;
